/**
 * Dosing & Instructions Step - Redesigned
 * 
 * Addresses Client Feedback:
 * - Renamed from "Physician Notes" to "Physician Instructions"
 * - Added instruction template reminder
 * - Smart quantity calculation based on frequency/duration
 * - Removed redundant fields (Administrator Name, Timing Instructions, Food Instructions, Activity Restrictions)
 * - Only Routine/PRN administration options
 * - Clear, focused interface
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Form,
    Input,
    InputNumber,
    Select,
    Button,
    Alert,
    Space,
    Table,
    Tag,
    Radio,
    Divider,
    Typography,
    message,
    TimePicker,
    Checkbox,
    Collapse,
    DatePicker
} from 'antd';
import {
    EditOutlined,
    CalculatorOutlined,
    CheckCircleOutlined,
    MedicineBoxOutlined,
    ClockCircleOutlined,
    ScheduleOutlined
} from '@ant-design/icons';
import { getCurrentPhysicianName, getCurrentUserId } from '../../../utils/authUtils';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;

const DosingInstructionsStep = ({ workflowData, onInstructionsComplete, loading }) => {
    const [form] = Form.useForm();
    const [medicationInstructions, setMedicationInstructions] = useState([]);
    const [currentEditingIndex, setCurrentEditingIndex] = useState(0);
    const [timingMode, setTimingMode] = useState('default');

    // Frequency options
    const frequencyOptions = [
        { value: 'once_daily', label: 'Once daily', timesPerDay: 1 },
        { value: 'twice_daily', label: 'Twice daily', timesPerDay: 2 },
        { value: 'three_times_daily', label: 'Three times daily', timesPerDay: 3 },
        { value: 'four_times_daily', label: 'Four times daily', timesPerDay: 4 },
        { value: 'every_4_hours', label: 'Every 4 hours', timesPerDay: 6 },
        { value: 'every_6_hours', label: 'Every 6 hours', timesPerDay: 4 },
        { value: 'every_8_hours', label: 'Every 8 hours', timesPerDay: 3 },
        { value: 'every_12_hours', label: 'Every 12 hours', timesPerDay: 2 },
        { value: 'with_meals', label: 'With meals', timesPerDay: 3 },
        { value: 'at_bedtime', label: 'At bedtime', timesPerDay: 1 },
        { value: 'as_needed', label: 'As needed (PRN)', timesPerDay: null }
    ];

    // Common administration routes
    const routeOptions = [
        'oral',
        'intravenous',
        'intramuscular',
        'subcutaneous',
        'topical',
        'transdermal',
        'inhalation',
        'ophthalmic',
        'otic',
        'rectal',
        'vaginal',
        'sublingual',
        'buccal'
    ];

    // Initialize with selected medications
    useEffect(() => {
        if (workflowData?.selected_medications?.length > 0) {
            const instructions = workflowData.selected_medications.map((medication) => {
                const defaultFrequency = 'twice_daily';
                const initialRoute = (Array.isArray(medication.route_of_administration)
                    ? medication.route_of_administration[0]
                    : medication.route_of_administration) || 'oral';
                return {
                    medication_id: medication.medication_id,
                    medication_name: medication.brand_name,
                    generic_name: medication.generic_name,
                    strength: medication.strength,
                    dosage_form: medication.dosage_form,
                    route: initialRoute,
                    dose_amount: '1',
                    dose_unit: getDoseUnit(medication.dosage_form),
                    frequency: defaultFrequency,
                    duration_days: 30,
                    start_date: dayjs().format('YYYY-MM-DD'),
                    end_date: dayjs().add(30, 'days').format('YYYY-MM-DD'),
                    calculated_quantity: 60,
                    physician_instructions: '',
                    administration_type: 'routine',
                    indication: '',
                    special_notes: '',
                    // Medication timing fields
                    use_default_timing: true,
                    custom_timing_enabled: false,
                    administration_times: getDefaultAdministrationTimes(defaultFrequency),
                    timing_notes: ''
                };
            });
            
            setMedicationInstructions(instructions);
            
            // Set form values for first medication
            if (instructions.length > 0) {
                setFormValuesForMedication(instructions[0]);
            }
        }
    }, [workflowData.selected_medications]);

    // Load existing instructions if available
    useEffect(() => {
        if (workflowData?.dosing_instructions?.medication_instructions) {
            setMedicationInstructions(workflowData.dosing_instructions.medication_instructions);
            if (workflowData.dosing_instructions.medication_instructions.length > 0) {
                setFormValuesForMedication(workflowData.dosing_instructions.medication_instructions[0]);
            }
        }
    }, [workflowData.dosing_instructions]);

    const getDoseUnit = (dosageForm) => {
        const unitMap = {
            'tablet': 'tablet(s)',
            'capsule': 'capsule(s)',
            'liquid': 'ml',
            'syrup': 'ml',
            'drops': 'drop(s)',
            'injection': 'ml',
            'cream': 'gram(s)',
            'ointment': 'gram(s)',
            'patch': 'patch(es)',
            'inhaler': 'puff(s)'
        };
        return unitMap[dosageForm] || 'unit(s)';
    };

    // Calculate default administration times based on frequency
    const getDefaultAdministrationTimes = (frequency) => {
        const timingMap = {
            'once_daily': ['09:00'],
            'twice_daily': ['09:00', '21:00'],
            'three_times_daily': ['08:00', '13:00', '18:00'],
            'four_times_daily': ['08:00', '13:00', '18:00', '22:00'],
            'every_4_hours': ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00'],
            'every_6_hours': ['06:00', '12:00', '18:00', '00:00'],
            'every_8_hours': ['08:00', '16:00', '00:00'],
            'every_12_hours': ['09:00', '21:00'],
            'with_meals': ['08:00', '13:00', '18:00'],
            'at_bedtime': ['22:00'],
            'as_needed': []
        };
        
        return timingMap[frequency] || [];
    };

    // Format administration times for display
    const formatAdministrationTimes = (times) => {
        if (!times || times.length === 0) return 'As needed';
        
        return times.map(time => {
            const [hour, minute] = time.split(':');
            const hour12 = parseInt(hour) > 12 ? parseInt(hour) - 12 : parseInt(hour);
            const period = parseInt(hour) >= 12 ? 'PM' : 'AM';
            const displayHour = hour12 === 0 ? 12 : hour12;
            return `${displayHour}:${minute} ${period}`;
        }).join(', ');
    };

    const setFormValuesForMedication = (medication) => {
        // Determine timing mode from existing data
        const medicationTimingMode = medication.use_default_timing !== false ? 'default' : 'custom';
        setTimingMode(medicationTimingMode);
        
        form.setFieldsValue({
            dose_amount: medication.dose_amount,
            frequency: medication.frequency,
            duration_days: medication.duration_days,
            start_date: medication.start_date ? dayjs(medication.start_date) : dayjs(),
            end_date: medication.end_date ? dayjs(medication.end_date) : null,
            physician_instructions: medication.physician_instructions,
            administration_type: medication.administration_type,
            indication: medication.indication,
            special_notes: medication.special_notes,
            route: medication.route || 'oral',
            use_default_timing: medication.use_default_timing !== undefined ? medication.use_default_timing : true,
            custom_timing_enabled: medication.custom_timing_enabled || false,
            timing_notes: medication.timing_notes || ''
        });
    };

    const calculateQuantity = (doseAmount, frequency, durationDays) => {
        const frequencyData = frequencyOptions.find(f => f.value === frequency);
        if (!frequencyData || !frequencyData.timesPerDay || !doseAmount || !durationDays) {
            return 0;
        }
        
        const dailyDoses = frequencyData.timesPerDay;
        const totalDoses = dailyDoses * durationDays;
        const quantity = totalDoses * parseFloat(doseAmount);
        
        return Math.ceil(quantity);
    };

    const calculateEndDate = (startDate, durationDays) => {
        if (!startDate || !durationDays) return null;
        return dayjs(startDate).add(durationDays, 'days').format('YYYY-MM-DD');
    };

    const handleFormValuesChange = (changedValues, allValues) => {
        // Update current medication with form values
        const updatedInstructions = [...medicationInstructions];
        const currentMedication = updatedInstructions[currentEditingIndex];
        
        if (currentMedication) {
            // Update the current medication
            Object.keys(allValues).forEach(key => {
                currentMedication[key] = allValues[key];
            });
            
            // Recalculate quantity if relevant fields changed
            if (changedValues.dose_amount || changedValues.frequency || changedValues.duration_days) {
                currentMedication.calculated_quantity = calculateQuantity(
                    allValues.dose_amount,
                    allValues.frequency,
                    allValues.duration_days
                );
            }
            // Ensure route persists
            if (changedValues.route !== undefined) {
                currentMedication.route = allValues.route;
            }

            // Recalculate end date if start date or duration changes
            if (changedValues.start_date || changedValues.duration_days) {
                const startDate = allValues.start_date ? allValues.start_date.format('YYYY-MM-DD') : null;
                const endDate = calculateEndDate(startDate, allValues.duration_days);
                
                if (endDate) {
                    currentMedication.end_date = endDate;
                    // Update form field to show calculated end date
                    form.setFieldValue('end_date', dayjs(endDate));
                    console.log(`Updated medication dates for ${currentMedication.medication_name}:`, {
                        start_date: startDate,
                        end_date: endDate,
                        duration_days: allValues.duration_days
                    });
                }
            }
            
            // Also update start_date if it changes directly
            if (changedValues.start_date) {
                const startDate = allValues.start_date ? allValues.start_date.format('YYYY-MM-DD') : null;
                currentMedication.start_date = startDate;
            }
            
            // Update administration times when frequency changes or timing preferences change
            if (changedValues.frequency || changedValues.use_default_timing) {
                if (allValues.use_default_timing === true || timingMode === 'default') {
                    // Use default timing for the frequency
                    currentMedication.administration_times = getDefaultAdministrationTimes(allValues.frequency);
                    currentMedication.use_default_timing = true;
                    currentMedication.custom_timing_enabled = false;
                }
                // If use_default_timing is false, keep existing custom times
            }
            
            // Handle custom timing toggle
            if (changedValues.custom_timing_enabled !== undefined) {
                if (changedValues.custom_timing_enabled || timingMode === 'custom') {
                    // Enable custom timing - start with default times as base if no custom times exist
                    if (!currentMedication.administration_times || currentMedication.administration_times.length === 0) {
                        currentMedication.administration_times = getDefaultAdministrationTimes(allValues.frequency);
                    }
                    currentMedication.use_default_timing = false;
                } else {
                    // Disable custom timing - revert to default
                    currentMedication.administration_times = getDefaultAdministrationTimes(allValues.frequency);
                    currentMedication.use_default_timing = true;
                }
            }
            
            setMedicationInstructions(updatedInstructions);
        }
    };

    // Handle timing mode changes
    const handleTimingModeChange = (e) => {
        const newMode = e.target.value;
        setTimingMode(newMode);
        
        const updatedInstructions = [...medicationInstructions];
        const currentMedication = updatedInstructions[currentEditingIndex];
        
        if (currentMedication) {
            if (newMode === 'default') {
                currentMedication.use_default_timing = true;
                currentMedication.custom_timing_enabled = false;
                currentMedication.administration_times = getDefaultAdministrationTimes(
                    form.getFieldValue('frequency') || currentMedication.frequency
                );
            } else if (newMode === 'custom') {
                currentMedication.use_default_timing = false;
                currentMedication.custom_timing_enabled = true;
                // Keep existing custom times if available, otherwise use default as starting point
                if (!currentMedication.administration_times || currentMedication.administration_times.length === 0) {
                    currentMedication.administration_times = getDefaultAdministrationTimes(
                        form.getFieldValue('frequency') || currentMedication.frequency
                    );
                }
            }
            
            setMedicationInstructions(updatedInstructions);
            
            // Update form values
            form.setFieldsValue({
                use_default_timing: currentMedication.use_default_timing,
                custom_timing_enabled: currentMedication.custom_timing_enabled
            });
        }
    };

    const switchToMedication = (index) => {
        setCurrentEditingIndex(index);
        setFormValuesForMedication(medicationInstructions[index]);
    };

    // Handle custom timing changes
    const handleTimingChange = (index, value) => {
        const updatedInstructions = [...medicationInstructions];
        const currentMedication = updatedInstructions[currentEditingIndex];
        
        if (currentMedication && currentMedication.administration_times) {
            const newTimes = [...currentMedication.administration_times];
            newTimes[index] = value ? value.format('hh:mm A') : null;
            currentMedication.administration_times = newTimes.filter(time => time !== null);
            setMedicationInstructions(updatedInstructions);
        }
    };

    // Add new custom timing slot
    const addCustomTime = () => {
        const updatedInstructions = [...medicationInstructions];
        const currentMedication = updatedInstructions[currentEditingIndex];
        
        if (currentMedication) {
            const newTimes = [...(currentMedication.administration_times || [])];
            newTimes.push('09:00'); // Default new time
            currentMedication.administration_times = newTimes;
            setMedicationInstructions(updatedInstructions);
        }
    };

    // Remove custom timing slot
    const removeCustomTime = (index) => {
        const updatedInstructions = [...medicationInstructions];
        const currentMedication = updatedInstructions[currentEditingIndex];
        
        if (currentMedication && currentMedication.administration_times) {
            const newTimes = [...currentMedication.administration_times];
            newTimes.splice(index, 1);
            currentMedication.administration_times = newTimes;
            setMedicationInstructions(updatedInstructions);
        }
    };

    const validateInstructions = () => {
        // Check if all medications have required instructions
        const incompleteInstructions = medicationInstructions.filter(med => 
            !med.physician_instructions?.trim() || 
            !med.dose_amount || 
            !med.frequency ||
            !med.duration_days
        );

        if (incompleteInstructions.length > 0) {
            return {
                isValid: false,
                message: `${incompleteInstructions.length} medication(s) have incomplete dosing instructions`
            };
        }

        return { isValid: true };
    };

    const handleProceedToNext = () => {
        const validation = validateInstructions();
        
        if (!validation.isValid) {
            message.error(validation.message);
            return;
        }

        // Validate that all medications have proper date fields
        const medicationsWithMissingDates = medicationInstructions.filter(med => 
            !med.start_date || !med.end_date
        );
        
        if (medicationsWithMissingDates.length > 0) {
            console.warn('Medications missing start/end dates:', medicationsWithMissingDates.map(m => m.medication_name));
            message.warning(`${medicationsWithMissingDates.length} medication(s) missing start or end dates. Please verify all date fields.`);
        }

        // Log final medication instructions with date validation
        console.log('Final medication instructions being passed to next step:', medicationInstructions.map(med => ({
            medication: med.medication_name,
            start_date: med.start_date,
            end_date: med.end_date,
            duration_days: med.duration_days,
            administration_times: med.administration_times
        })));

        const instructionsData = {
            medication_instructions: medicationInstructions,
            total_medications: medicationInstructions.length,
            instructions_completed_at: new Date().toISOString(),
            completed_by: getCurrentUserId() || getCurrentPhysicianName()
        };

        onInstructionsComplete(instructionsData);
    };

    // Render medication selection tabs
    const renderMedicationTabs = () => (
        <Card size="small" title="Selected Medications" style={{ marginBottom: 16 }}>
            <Space wrap>
                {medicationInstructions.map((medication, index) => {
                    const isComplete = medication.physician_instructions?.trim() && 
                                     medication.dose_amount && 
                                     medication.frequency && 
                                     medication.duration_days;
                    
                    return (
                        <Button
                            key={medication.medication_id}
                            type={index === currentEditingIndex ? 'primary' : 'default'}
                            size="small"
                            onClick={() => switchToMedication(index)}
                            icon={isComplete ? <CheckCircleOutlined /> : <EditOutlined />}
                        >
                            {medication.medication_name}
                            {isComplete && <Tag color="green" style={{ marginLeft: 4 }}>Complete</Tag>}
                        </Button>
                    );
                })}
            </Space>
        </Card>
    );

    // Render instruction template reminder
    const renderInstructionTemplate = () => (
        <Alert
            message="Physician Instructions Template"
            description={
                <div>
                    <Text strong style={{ color: '#ff4d4f' }}>
                        Please include: Sig/Instruction, Dosage, Units, Frequency, Number of Days, and any Additional Instructions
                    </Text>
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                        Example: "Take 1 tablet by mouth twice daily with meals for diabetes management. 
                        Monitor blood glucose levels. Complete full course as prescribed."
                    </div>
                </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
        />
    );

    // Render dosing form for current medication
    const renderDosingForm = () => {
        const currentMedication = medicationInstructions[currentEditingIndex];
        
        if (!currentMedication) return null;

        return (
            <Card 
                title={
                    <Space>
                        <MedicineBoxOutlined />
                        Dosing Instructions: {currentMedication.medication_name}
                    </Space>
                }
            >
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <Alert
                            message={`${currentMedication.medication_name} (${currentMedication.generic_name})`}
                            description={`${currentMedication.strength.value}${currentMedication.strength.unit} ${currentMedication.dosage_form}`}
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    </Col>
                </Row>

                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handleFormValuesChange}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                label="Route of Administration"
                                name="route"
                                rules={[
                                    { required: true, message: 'Route is required' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value) return Promise.resolve();
                                            // Validate vaginal route usage against dosage form
                                            const df = (getFieldValue('dosage_form') || '').toLowerCase();
                                            if (value === 'vaginal') {
                                                const allowed = ['tablet', 'suppository', 'cream', 'gel', 'ring', 'capsule'];
                                                if (!allowed.includes(df)) {
                                                    return Promise.reject(new Error('Vaginal route is incompatible with selected dosage form.'));
                                                }
                                            }
                                            return Promise.resolve();
                                        }
                                    })
                                ]}
                            >
                                <Select placeholder="Select route">
                                    {routeOptions.map(rt => (
                                        <Option key={rt} value={rt}>{rt}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                label="Dose Amount"
                                name="dose_amount"
                                rules={[{ required: true, message: 'Dose amount is required' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0.1}
                                    step={0.5}
                                    precision={1}
                                    addonAfter={currentMedication.dose_unit}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={8}>
                            <Form.Item
                                label="Frequency"
                                name="frequency"
                                rules={[{ required: true, message: 'Frequency is required' }]}
                            >
                                <Select placeholder="Select frequency">
                                    {frequencyOptions.map(freq => (
                                        <Option key={freq.value} value={freq.value}>
                                            {freq.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={8}>
                            <Form.Item
                                label="Duration (Days)"
                                name="duration_days"
                                rules={[{ required: true, message: 'Duration is required' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1}
                                    max={365}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Start Date and End Date Fields */}
                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Start Date"
                                name="start_date"
                                rules={[{ required: true, message: 'Start date is required' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="Select start date"
                                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                                />
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="End Date (Calculated)"
                                name="end_date"
                                help="Automatically calculated based on start date + duration"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    placeholder="End date (auto-calculated)"
                                    disabled
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Calculated Quantity Display */}
                    <Row>
                        <Col xs={24}>
                            <Alert
                                message={
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Space>
                                            <CalculatorOutlined />
                                            Calculated Quantity: {currentMedication.calculated_quantity} {currentMedication.dose_unit}
                                        </Space>
                                        {currentMedication.start_date && currentMedication.end_date && (
                                            <Space>
                                                <ClockCircleOutlined />
                                                Duration: {dayjs(currentMedication.start_date).format('DD/MM/YYYY')} to {dayjs(currentMedication.end_date).format('DD/MM/YYYY')} ({currentMedication.duration_days} days)
                                            </Space>
                                        )}
                                    </Space>
                                }
                                type="success"
                                size="small"
                                style={{ marginBottom: 16 }}
                            />
                        </Col>
                    </Row>

                    <Divider />

                    {/* Physician Instructions */}
                    <Row>
                        <Col xs={24}>
                            {renderInstructionTemplate()}
                            
                            <Form.Item
                                label="Physician Instructions*"
                                name="physician_instructions"
                                rules={[
                                    { required: true, message: 'Physician instructions are required' },
                                    { min: 20, message: 'Instructions must be at least 20 characters' }
                                ]}
                            >
                                <TextArea
                                    rows={4}
                                    placeholder="Enter detailed instructions including sig, dosage, frequency, duration, and any special notes..."
                                    showCount
                                    maxLength={500}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Administration Type"
                                name="administration_type"
                                rules={[{ required: true, message: 'Administration type is required' }]}
                            >
                                <Radio.Group>
                                    <Radio value="routine">Routine (Scheduled)</Radio>
                                    <Radio value="prn">PRN (As Needed)</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                        
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Indication (Optional)"
                                name="indication"
                            >
                                <Input placeholder="Primary indication for medication" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={24}>
                            <Form.Item
                                label="Special Notes (Optional)"
                                name="special_notes"
                            >
                                <TextArea
                                    rows={2}
                                    placeholder="Any additional clinical notes or special considerations..."
                                    maxLength={200}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Medication Administration Timing Section */}
                    <Divider />
                    <Row>
                        <Col xs={24}>
                            {renderMedicationTimingSection(currentMedication)}
                        </Col>
                    </Row>
                </Form>
            </Card>
        );
    };

    // Render medication timing section
    const renderMedicationTimingSection = (currentMedication) => {
        if (!currentMedication) return null;

        const frequency = form.getFieldValue('frequency') || currentMedication.frequency;
        const adminType = form.getFieldValue('administration_type') || currentMedication.administration_type;
        const useDefaultTiming = form.getFieldValue('use_default_timing') !== undefined ? 
            form.getFieldValue('use_default_timing') : currentMedication.use_default_timing;
        const customTimingEnabled = form.getFieldValue('custom_timing_enabled') || currentMedication.custom_timing_enabled;
        
        // Don't show timing for PRN medications
        if (adminType === 'prn' || frequency === 'as_needed') {
            return (
                <Alert
                    message="PRN Medication"
                    description="This medication is prescribed as needed (PRN). No specific administration times are required."
                    type="info"
                    showIcon
                    icon={<ClockCircleOutlined />}
                />
            );
        }

        const defaultTimes = getDefaultAdministrationTimes(frequency);

        return (
            <Card
                title={
                    <Space>
                        <ScheduleOutlined />
                        Administration Timing
                    </Space>
                }
                size="small"
                style={{ background: '#fafafa' }}
            >
                {/* Default timing display */}
                <Row style={{ marginBottom: 16 }}>
                    <Col xs={24}>
                        <Alert
                            message={`Default ${frequencyOptions.find(f => f.value === frequency)?.label || frequency} Schedule`}
                            description={
                                <div>
                                    <Text strong>Recommended Times: </Text>
                                    <Text>{formatAdministrationTimes(defaultTimes)}</Text>
                                </div>
                            }
                            type="info"
                            showIcon
                            icon={<ClockCircleOutlined />}
                        />
                    </Col>
                </Row>

                {/* Timing mode selection */}
                <Row>
                    <Col xs={24}>
                        <fieldset style={{ border: 'none', padding: 0, marginBottom: 16 }}>
                            <legend style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: 8 }}>
                                Choose timing mode:
                            </legend>
                            <Radio.Group 
                                name="timingMode" 
                                value={timingMode} 
                                onChange={handleTimingModeChange}
                                style={{ marginBottom: 8 }}
                            >
                                <Radio value="default">Default timing</Radio>
                                <Radio value="custom">Custom timing</Radio>
                            </Radio.Group>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                                Choose one timing mode. Only the selected mode will be applied.
                            </div>
                        </fieldset>
                    </Col>
                </Row>

                {/* Default timing panel - always visible */}
                <Row style={{ marginBottom: 16 }}>
                    <Col xs={24}>
                        <div style={{ 
                            padding: '12px', 
                            border: `1px solid ${timingMode === 'default' ? '#1890ff' : '#d9d9d9'}`,
                            borderRadius: '6px',
                            backgroundColor: timingMode === 'default' ? '#f0f8ff' : '#fafafa',
                            opacity: timingMode === 'default' ? 1 : 0.7
                        }}>
                            <div style={{ marginBottom: 8 }}>
                                <Text strong style={{ color: timingMode === 'default' ? '#1890ff' : '#666' }}>
                                    Default Schedule: {frequencyOptions.find(f => f.value === frequency)?.label}
                                </Text>
                            </div>
                            <div>
                                <Text style={{ color: timingMode === 'default' ? '#000' : '#666' }}>
                                    Recommended Times: {formatAdministrationTimes(defaultTimes)}
                                </Text>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Custom timing panel - always visible */}
                <Row>
                    <Col xs={24}>
                        <div style={{ 
                            padding: '12px', 
                            border: `1px solid ${timingMode === 'custom' ? '#1890ff' : '#d9d9d9'}`,
                            borderRadius: '6px',
                            backgroundColor: timingMode === 'custom' ? '#f0f8ff' : '#fafafa',
                            opacity: timingMode === 'custom' ? 1 : 0.7
                        }}>
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: timingMode === 'custom' ? '#1890ff' : '#666' }}>
                                    Custom Administration Times
                                </Text>
                            </div>

                            <div style={{ marginLeft: timingMode === 'custom' ? 0 : 0, marginBottom: 16 }}>
                                <div style={{ marginBottom: 8 }}>
                                    <Text style={{ color: timingMode === 'custom' ? '#000' : '#666' }}>
                                        Administration Times:
                                    </Text>
                                </div>
                                
                                {(currentMedication.administration_times || defaultTimes).map((time, index) => (
                                    <Row key={index} gutter={[8, 8]} style={{ marginBottom: 8 }}>
                                        <Col xs={16} sm={12}>
                                            <TimePicker
                                                value={time ? dayjs(time, 'hh:mm A') : null}
                                                format="hh:mm A"
                                                onChange={(value) => handleTimingChange(index, value)}
                                                style={{ width: '100%' }}
                                                placeholder="Select time"
                                                disabled={timingMode !== 'custom'}
                                            />
                                        </Col>
                                        <Col xs={8} sm={4}>
                                            {(currentMedication.administration_times || defaultTimes).length > 1 && (
                                                <Button
                                                    type="text"
                                                    danger
                                                    size="small"
                                                    onClick={() => removeCustomTime(index)}
                                                    disabled={timingMode !== 'custom'}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                ))}
                                
                                <Button
                                    type="dashed"
                                    size="small"
                                    onClick={addCustomTime}
                                    style={{ marginTop: 8 }}
                                    disabled={timingMode !== 'custom'}
                                >
                                    Add Another Time
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Timing notes */}
                <Row style={{ marginTop: 16 }}>
                    <Col xs={24}>
                        <Form.Item
                            label="Timing Notes (Optional)"
                            name="timing_notes"
                            style={{ marginBottom: 0 }}
                        >
                            <TextArea
                                rows={2}
                                placeholder="Special timing instructions (e.g., with meals, before bedtime, etc.)"
                                maxLength={150}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>
        );
    };

    // Render instruction summary
    const renderInstructionSummary = () => {
        const completedInstructions = medicationInstructions.filter(med => 
            med.physician_instructions?.trim() && med.dose_amount && med.frequency && med.duration_days
        );

        const columns = [
            {
                title: 'Medication',
                key: 'medication',
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{record.medication_name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.generic_name}
                        </div>
                    </div>
                )
            },
            {
                title: 'Dosing',
                key: 'dosing',
                render: (_, record) => (
                    <div>
                        <div>{record.dose_amount} {record.dose_unit}</div>
                        <div style={{ fontSize: '12px' }}>
                            {frequencyOptions.find(f => f.value === record.frequency)?.label}
                        </div>
                        <div style={{ fontSize: '12px' }}>
                            Duration: {record.duration_days} days
                        </div>
                    </div>
                )
            },
            {
                title: 'Quantity',
                dataIndex: 'calculated_quantity',
                key: 'calculated_quantity',
                render: (quantity, record) => (
                    <Tag color="blue">{quantity} {record.dose_unit}</Tag>
                )
            },
            {
                title: 'Type',
                dataIndex: 'administration_type',
                key: 'administration_type',
                render: (type) => (
                    <Tag color={type === 'routine' ? 'green' : 'orange'}>
                        {type === 'routine' ? 'Routine' : 'PRN'}
                    </Tag>
                )
            },
            {
                title: 'Timing',
                key: 'timing',
                render: (_, record) => {
                    if (record.administration_type === 'prn' || record.frequency === 'as_needed') {
                        return <Text type="secondary">As needed</Text>;
                    }
                    
                    const times = record.administration_times || getDefaultAdministrationTimes(record.frequency);
                    const displayTimes = formatAdministrationTimes(times);
                    
                    return (
                        <div>
                            <div style={{ fontSize: '12px' }}>{displayTimes}</div>
                            {record.custom_timing_enabled && (
                                <Tag size="small" color="purple">Custom</Tag>
                            )}
                        </div>
                    );
                }
            },
            {
                title: 'Status',
                key: 'status',
                render: (_, record) => {
                    const isComplete = record.physician_instructions?.trim() && 
                                     record.dose_amount && record.frequency && record.duration_days;
                    return (
                        <Tag color={isComplete ? 'green' : 'orange'}>
                            {isComplete ? 'Complete' : 'Incomplete'}
                        </Tag>
                    );
                }
            }
        ];

        return (
            <Card 
                title="Instruction Summary"
                extra={
                    <Tag color="blue">
                        {completedInstructions.length} of {medicationInstructions.length} complete
                    </Tag>
                }
                style={{ marginTop: 16 }}
            >
                <Table
                    columns={columns}
                    dataSource={medicationInstructions}
                    rowKey="medication_id"
                    size="small"
                    pagination={false}
                />
            </Card>
        );
    };

    return (
        <div className="dosing-instructions-step">
            {/* Medication Selection Tabs */}
            {renderMedicationTabs()}

            {/* Dosing Form */}
            {renderDosingForm()}

            {/* Instruction Summary */}
            {renderInstructionSummary()}

            {/* Action Buttons */}
            <Row justify="end" style={{ marginTop: 24 }}>
                <Col>
                    <Button 
                        type="primary" 
                        size="large"
                        onClick={handleProceedToNext}
                        loading={loading}
                        disabled={medicationInstructions.filter(med => 
                            med.physician_instructions?.trim() && med.dose_amount && med.frequency && med.duration_days
                        ).length === 0}
                    >
                        Proceed to Final Review
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default DosingInstructionsStep;